import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  ClipboardCheck,
  Edit,
  Filter,
  Globe,
  GraduationCap,
  Paperclip,
  Plus,
  Search,
  Stethoscope,
  Trash2,
  Users,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAllRubricTemplates, useDeleteRubricTemplate, RubricTemplate } from '@/hooks/useRubricTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';

const categoryLabels: Record<string, { label: string; icon: typeof Stethoscope; color: string }> = {
  patient: { label: 'Patient', icon: Stethoscope, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100' },
  customer: { label: 'Customer', icon: Briefcase, color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-100' },
  coach: { label: 'Coach', icon: UserRound, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-100' },
  examiner: { label: 'Examiner', icon: GraduationCap, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-100' }
};

export default function RubricTemplates() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { data: templates = [], isLoading } = useAllRubricTemplates();
  const deleteTemplate = useDeleteRubricTemplate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; template: RubricTemplate | null }>({ open: false, template: null });

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (template.description?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const stats = {
    patient: templates.filter(t => t.category === 'patient').length,
    customer: templates.filter(t => t.category === 'customer').length,
    coach: templates.filter(t => t.category === 'coach').length,
    examiner: templates.filter(t => t.category === 'examiner').length
  };

  const handleDelete = () => {
    if (deleteModal.template) {
      deleteTemplate.mutate(deleteModal.template.id);
      setDeleteModal({ open: false, template: null });
    }
  };

  return (
    <div className="medd-page">
      <div className="medd-page-inner space-y-6">
        {/* Header */}
        <div className="medd-page-header">
          <div>
            <h1 className="medd-page-title">Rubric Templates</h1>
            <p className="medd-page-subtitle">
              Maintain consistent scoring criteria for simulations, coaching, and assessment workflows.
            </p>
          </div>
          {hasRole('admin') && (
            <Button onClick={() => navigate('/admin/rubric-templates/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          )}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {Object.entries(categoryLabels).map(([key, { label, icon: Icon, color }]) => (
            <Card
              key={key}
              className={`medd-stat-card cursor-pointer ${categoryFilter === key ? 'border-primary bg-primary/5' : ''}`}
              onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="medd-icon-tile bg-muted text-primary">
                    <Icon size={18} />
                  </div>
                  <span className="font-semibold">{label}</span>
                </div>
                <Badge variant="secondary" className={color}>
                  {stats[key as keyof typeof stats]}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="medd-command-panel flex flex-col gap-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(categoryLabels).map(([key, { label, icon }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader><div className="h-6 bg-muted rounded w-3/4" /></CardHeader>
                <CardContent><div className="h-20 bg-muted rounded" /></CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredTemplates.map(template => {
              const category = categoryLabels[template.category];
              const CategoryIcon = category?.icon || ClipboardCheck;
              const criteriaCount = Array.isArray(template.criteria) ? template.criteria.length : 0;
              const totalWeight = Array.isArray(template.criteria) 
                ? template.criteria.reduce((sum, c) => sum + (c.weight || 0), 0) 
                : 0;
              
              return (
                <Card key={template.id} className="hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg transition-all">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {template.description || 'No description'}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${category?.color || 'bg-muted text-foreground'} gap-1`}>
                        <CategoryIcon className="h-3 w-3" />
                        {category?.label || template.category}
                      </Badge>
                      <Badge variant="secondary">
                        {criteriaCount} criteria
                      </Badge>
                      {totalWeight === 100 && (
                          <Badge variant="outline" className="text-green-600">100%</Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {template.assign_to === 'all' ? (
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" /> All users
                        </span>
                      ) : template.assign_to === 'specific_users' ? (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> Specific users
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> Restricted access
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Paperclip className="w-3 h-3" />
                      {(template.knowledge_sources?.length || 0) > 0
                        ? `${template.knowledge_sources.length} knowledge file${template.knowledge_sources.length === 1 ? '' : 's'}`
                        : 'No knowledge files'}
                    </div>

                    {hasRole('admin') && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/admin/rubric-templates/edit/${template.id}`)}
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteModal({ open: true, template })}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {filteredTemplates.length === 0 && !isLoading && (
          <Card className="medd-empty-state">
            <p className="text-muted-foreground">No templates found matching your criteria</p>
          </Card>
        )}

        {deleteModal.template && (
          <DeleteConfirmModal
            open={deleteModal.open}
            agentName={deleteModal.template.name}
            onConfirm={handleDelete}
            onCancel={() => setDeleteModal({ open: false, template: null })}
          />
        )}
      </div>
    </div>
  );
}
